(function () {
  "use strict";

  // ---------------------------------------------------------------------------
  // Constitution sort — Hildegard's OWN markers only. No Galen, no classical
  // labels, no hot/cold/moist/dry axes.
  //
  // Causae et Curae describes four types of WOMEN by a cluster of observable
  // signs (complexion, flesh/build, disposition). We score each marker question
  // as a vote for one constitution and take the highest total — a "configural"
  // read of co-occurring signs, which is how Hildegard actually diagnoses. The
  // four keys are Latin marker words from her text (clara / viridis / prudens /
  // glauca), not the editorial Galenic rubrics.
  //
  // Each diagnostic question carries a numeric `weight`; its answers carry a
  // `temperament` (single cluster key) or `temperaments` (array — a PAIR-VOTE that
  // adds the weight to two keys). Weights: flesh-type 3 (load-bearing) · disposition
  // 1 + 1 (TWO questions — warmth + decision-making — combined 2, the tiebreaker) ·
  // undertone 1 (a low nudge; cool → clara & glauca). Questions flagged `tiebreaker:
  // true` (the two disposition questions) are summed into a disposition-tally; on an
  // equal max, the tie breaks toward the tied type with the most disposition support.
  // Clara is only the all-zero fallback.
  // See research/hildegard/causae/constitution-review/0-rules.md §G for the scheme.
  // ---------------------------------------------------------------------------
  const TEMPERAMENT_ORDER = ["clara", "viridis", "prudens", "glauca"];

  // `responses` = customer answer map (questionId → answerId | answerId[]).
  // Only questions with a numeric `weight` vote; their answers carry `temperament`.
  function tallyTemperaments(responses, questions) {
    const tally = { clara: 0, viridis: 0, prudens: 0, glauca: 0 };
    (questions || []).forEach(function (q) {
      if (!q || typeof q.weight !== "number") return; // non-marker questions abstain
      const r = responses ? responses[q.id] : undefined;
      if (r == null) return;
      const picked = Array.isArray(r) ? r : [r];
      picked.forEach(function (ansId) {
        const ans = (q.answers || []).find(function (a) { return a.id === ansId; });
        if (!ans) return;
        // `temperaments` (array) = pair-vote; `temperament` (string) = single vote.
        const keys = Array.isArray(ans.temperaments)
          ? ans.temperaments
          : (ans.temperament ? [ans.temperament] : []);
        keys.forEach(function (key) {
          if (tally.hasOwnProperty(key)) tally[key] += q.weight;
        });
      });
    });
    return tally;
  }

  // Highest total wins. On an equal max, the tie is broken toward the tied type with
  // the most DISPOSITION support — `dispTally` is the sum over the tiebreaker-flagged
  // (disposition) questions, so the load-bearing flesh vote leads and disposition is the
  // configural tiebreaker. A further tie (equal disposition support among the tied) falls
  // to TEMPERAMENT_ORDER (clara-first). An all-zero tally (no markers answered) → clara.
  function pickTemperament(tally, dispTally) {
    dispTally = dispTally || {};
    let max = -Infinity;
    TEMPERAMENT_ORDER.forEach(function (k) { if (tally[k] > max) max = tally[k]; });
    if (max <= 0) return "clara";
    const tied = TEMPERAMENT_ORDER.filter(function (k) { return tally[k] === max; });
    if (tied.length === 1) return tied[0];
    // tied is in TEMPERAMENT_ORDER, so the first scanned wins an equal-disposition tie (clara-first).
    let best = tied[0];
    let bestDisp = dispTally[tied[0]] || 0;
    tied.forEach(function (k) {
      const d = dispTally[k] || 0;
      if (d > bestDisp) { bestDisp = d; best = k; }
    });
    return best;
  }

  function cap(s) { return s ? s.charAt(0).toUpperCase() + s.slice(1) : s; }

  // ---------------------------------------------------------------------------
  // isConstitutionTie — true when the running constitution tally has an exact tie
  // at the top (max > 0, two or more types level). Drives the conditional vein
  // tiebreaker question: Quiz.jsx shows that step only when this returns true.
  //
  // Questions flagged `showIfTie` (the vein question itself) are EXCLUDED from the
  // tally here, so the show/hide decision never depends on the vein answer — that
  // keeps it stable and prevents any show↔hide flicker when veins is answered.
  // ---------------------------------------------------------------------------
  function isConstitutionTie(answers, config) {
    config = config || window;
    const Q = (config.HH_DIAGNOSIS_QUESTIONS || [])
      .filter(function (q) { return q && !q.showIfTie; });
    const tally = tallyTemperaments(answers, Q);
    let max = -Infinity;
    TEMPERAMENT_ORDER.forEach(function (k) { if (tally[k] > max) max = tally[k]; });
    if (max <= 0) return false;
    const tied = TEMPERAMENT_ORDER.filter(function (k) { return tally[k] === max; });
    return tied.length > 1;
  }

  // ---------------------------------------------------------------------------
  // computeMoon — pure astronomical / date calculation. No DOM, no config.
  //
  // info = { birthDate, term, weeks, dueDate }
  //   birthDate / dueDate are "YYYY-MM-DD" strings (dueDate optional)
  //   term in "ontime" | "early" | "late"; weeks optional number
  //
  // Returns { conception, lunarAge, phaseIndex, phaseKey, season, isEstimate }
  // or null when neither birthDate nor dueDate is usable. UTC throughout.
  // ---------------------------------------------------------------------------
  const DAY_MS = 86400000;
  const SYNODIC = 29.530588853; // days per synodic month
  const MOON_EPOCH_MS = Date.UTC(2000, 0, 6, 18, 14); // reference new moon
  const PHASE_KEYS = [
    "new", "waxing_crescent", "first_quarter", "waxing_gibbous",
    "full", "waning_gibbous", "last_quarter", "waning_crescent",
  ];

  // Parse "YYYY-MM-DD" as a UTC-midnight epoch ms. Returns null if unusable.
  function parseUTCDate(str) {
    if (typeof str !== "string") return null;
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(str);
    if (!m) return null;
    const y = parseInt(m[1], 10);
    const mo = parseInt(m[2], 10);
    const d = parseInt(m[3], 10);
    const ms = Date.UTC(y, mo - 1, d);
    if (isNaN(ms)) return null;
    return ms;
  }

  function fmtUTCDate(ms) {
    const d = new Date(ms);
    const y = d.getUTCFullYear();
    const mo = String(d.getUTCMonth() + 1);
    const da = String(d.getUTCDate());
    return y + "-" + (mo.length < 2 ? "0" + mo : mo) + "-" + (da.length < 2 ? "0" + da : da);
  }

  function seasonForMonth(month0) {
    // month0 is 0-indexed UTC month of conception (Northern Hemisphere).
    if (month0 >= 2 && month0 <= 4) return "spring";  // Feb-Apr
    if (month0 >= 5 && month0 <= 7) return "summer";  // May-Jul
    if (month0 >= 8 && month0 <= 10) return "autumn"; // Aug-Oct
    return "winter";                                  // Nov, Dec, Jan
  }

  function computeMoon(info) {
    info = info || {};

    // Step 1 - estimate conception date (UTC, day precision).
    let conceptionMs = null;
    const dueMs = parseUTCDate(info.dueDate);
    if (dueMs !== null) {
      // A due date already encodes timing; ignore term/weeks.
      conceptionMs = dueMs - 266 * DAY_MS;
    } else {
      const birthMs = parseUTCDate(info.birthDate);
      if (birthMs === null) return null;
      let ga;
      if (info.term === "early") ga = 40 - (info.weeks || 2);
      else if (info.term === "late") ga = 40 + (info.weeks || 1);
      else ga = 40; // "ontime" or unspecified
      const daysBack = ga * 7 - 14;
      conceptionMs = birthMs - daysBack * DAY_MS;
    }

    // Step 2 - moon phase at conception (8 phases).
    const daysSince = (conceptionMs - MOON_EPOCH_MS) / DAY_MS;
    const lunarAge = ((daysSince % SYNODIC) + SYNODIC) % SYNODIC;
    const phaseIndex = Math.floor((lunarAge / SYNODIC) * 8 + 0.5) % 8;
    const phaseKey = PHASE_KEYS[phaseIndex];

    // Step 3 - season of conception (Northern Hemisphere, by month).
    const season = seasonForMonth(new Date(conceptionMs).getUTCMonth());

    return {
      conception: fmtUTCDate(conceptionMs),
      lunarAge: lunarAge,
      phaseIndex: phaseIndex,
      phaseKey: phaseKey,
      season: season,
      isEstimate: true,
    };
  }

  // NOTE: `quizAnswers` is currently UNUSED — humor, noun, stone, and eye readings
  // all derive from `diagnosisAnswers`. Kept in the signature for call-site compatibility.
  function computeDiagnosis(diagnosisAnswers, quizAnswers, config) {
    config = config || window;
    const Q        = config.HH_DIAGNOSIS_QUESTIONS || [];
    const HUMORS   = config.HH_HUMORS || {};
    const STONES   = config.HH_STONES || {};       // keyed by the `ease` question's answer ids
    const CONSTITUTION_STONES = config.HH_CONSTITUTION_STONES || {}; // keyed by humorKey — the fallback
    const STONE_FALLBACK = config.HH_STONE_FALLBACK || null;
    const EYEP      = config.HH_EYE_PERSONALITIES || {};
    const RING_NOTE = config.HH_EYE_RING_NOTE || null;

    const tally    = tallyTemperaments(diagnosisAnswers, Q);
    // Tiebreaker = disposition. Sum just the disposition (tiebreaker-flagged) questions so
    // an equal-max tie breaks toward the type with the most disposition support.
    const dispTally = tallyTemperaments(diagnosisAnswers, (Q || []).filter(function (q) { return q && q.tiebreaker; }));
    const humorKey = pickTemperament(tally, dispTally);
    const humor    = HUMORS[humorKey] || { key: humorKey };

    const easeRaw = diagnosisAnswers ? diagnosisAnswers.ease : undefined;
    const easeIds = Array.isArray(easeRaw) ? easeRaw : (easeRaw ? [easeRaw] : []);
    // A stone for the complaint if one maps; otherwise the constitution's own stone (chosen for
    // what the type LACKS); STONE_FALLBACK is the last resort if neither resolves.
    const complaintStone = easeIds.reduce(function (found, id) { return found || STONES[id] || null; }, null);
    const stone = complaintStone || CONSTITUTION_STONES[humorKey] || STONE_FALLBACK || null;

    // Eye-colour personality — a SEPARATE axis. The base eye colour gives a Latin
    // noun + a reusable clause; the archetype name is `[constitution] + [eye noun]`
    // (e.g. "Clara Perfectrix") and the hero folds the clause onto the constitution.
    const eyeKey    = diagnosisAnswers ? diagnosisAnswers.eye_color : undefined;
    const eye       = (eyeKey && EYEP[eyeKey]) || EYEP._default || { noun: "", clause: "" };
    const eyeNoun     = eye.noun || "";
    const eyeClause   = eye.clause || "";       // short personality line → folded into the hero
    const eyeAccordion = eye.accordion || "";   // richer physical-first copy → the eye panel
    const eyeColor    = eye.color || "";        // modern colour name → a physical marker on the card

    // Archetype gloss = composed from the constitution adjective + the eye role-noun
    // ("the {glossAdj} {glossRole}" → e.g. "the reserved discerner"). No eye answered →
    // the constitution's own gloss.
    const archetypeGloss = (eye.glossRole && humor.glossAdj)
      ? ("the " + humor.glossAdj + " " + eye.glossRole)
      : (humor.gloss || "");

    // Ring overlay — a "yes" on eye_ring adds an UNNAMED note inside the eye accordion
    // (the old "Firebrand" label is retired). It changes neither the name nor the hero.
    const hasRing  = !!(diagnosisAnswers && diagnosisAnswers.eye_ring === "yes");
    const ringNote = hasRing ? RING_NOTE : null;

    const constitutionLabel = humor.label || cap(humorKey);
    // Archetype name = constitution + eye noun ("Clara Perfectrix"); constitution alone if no eye.
    const identityName = constitutionLabel + (eyeNoun ? " " + eyeNoun : "");
    // Hero = the constitution reading with the eye-colour clause folded on.
    const heroReading = (humor.reading || "") + (eyeClause ? " " + eyeClause : "");

    // Moon overlay — conception-date moon phase + season. note/caution config
    // maps don't exist yet; the lookups are null-safe so they return null today.
    const moonInfo = (diagnosisAnswers) ? computeMoon({
      birthDate: diagnosisAnswers.birth_date,
      term:      diagnosisAnswers.term,
      weeks:     diagnosisAnswers.term_weeks,
      dueDate:   diagnosisAnswers.due_date,
    }) : null;
    const moon = moonInfo ? {
      phaseKey:   moonInfo.phaseKey,
      season:     moonInfo.season,
      conception: moonInfo.conception,
      isEstimate: moonInfo.isEstimate,
      note:    (config.HH_MOON_PHASES    || {})[moonInfo.phaseKey] || null,
      caution: (config.HH_SEASON_CAUTIONS || {})[moonInfo.season]  || null,
    } : null;

    return {
      humorKey: humorKey, humor: humor, gloss: archetypeGloss,
      identityName: identityName, heroReading: heroReading,
      eyeNoun: eyeNoun, eyeClause: eyeClause, eyeAccordion: eyeAccordion, eyeColor: eyeColor,
      eyeKey: eyeKey, // raw eye_color id (blue_grey | hazel | green | brown) — keys the per-colour C&C passage
      eyeRing: hasRing, ringNote: ringNote,
      stone: stone,
      tally: tally,
      moon: moon,
      birthDate: (diagnosisAnswers && diagnosisAnswers.birth_date) || null,
      undertone: (diagnosisAnswers && diagnosisAnswers.complexion) || null, // cool | warm | neutral — drives the card image
    };
  }

  window.computeMoon = computeMoon;
  window.computeDiagnosis = computeDiagnosis;
  window.isConstitutionTie = isConstitutionTie;
})();
